// ═══════════════════════════════════════════════════════════════
// 🌶️ EMAIL BLAST CENTER
// Send beautiful broadcast emails to LCA notary network
// ═══════════════════════════════════════════════════════════════

import React, { useState, useEffect } from 'react'
import { supabase, supabaseAdmin } from '../lib/supabase'
import { useAuth } from '../hooks/useAuth'
import toast from 'react-hot-toast'

// ═══════════════════════════════════════════════════════════════
// 📚 PRE-BUILT EMAIL TEMPLATES
// ═══════════════════════════════════════════════════════════════
const EMAIL_TEMPLATES = {
  custom: {
    label:   '✏️ Blank / Custom',
    subject: '',
    body:    '',
  },
  welcome: {
    label:   '🎉 Welcome New Notary',
    subject: 'Welcome to the LCA Family!! 🎉',
    body: `Hi [FIRST_NAME],

Welcome to Loan Closing Agent LLC!! 🎉

We are absolutely thrilled to have you join our nationwide network of trusted notary professionals!!

As your CEO, I want you to know that you're not just a vendor — you're part of our family. We believe in supporting our notaries every step of the way!!

Your vendor number is: [VENDOR]

Here's what to expect:
• Job assignments via email & SMS
• Direct deposit payments
• 24/7 platform access
• Direct support from our team
• Monthly virtual meetings (coming soon!)

If you ever need ANYTHING — questions, support, or just to chat — don't hesitate to reach out!!

Welcome aboard!! 🚀

Warmly,
Nancy Phillips
Owner & CEO
Loan Closing Agent LLC`,
  },
  monthly_recap: {
    label:   '📊 Monthly Network Update',
    subject: 'LCA Monthly Recap — News, Tips & Thanks!!',
    body: `Hi [FIRST_NAME],

Hope you're having an amazing month!!

I wanted to take a moment to share some exciting updates from the LCA family:

🎯 NETWORK UPDATES:
• [Add updates here]

📚 NEW RESOURCES:
• [Add resources here]

🌟 SHOUT-OUTS:
• [Add notary recognitions here]

💪 LOOKING AHEAD:
• [Add upcoming events/news here]

As always, THANK YOU for being part of our nationwide network. Your professionalism and dedication are what make LCA the trusted choice for our clients across the country!!

Until next month!!

Warmly,
Nancy Phillips
Owner & CEO
Loan Closing Agent LLC`,
  },
  meeting_invitation: {
    label:   '📅 Virtual Meeting Invitation',
    subject: '📅 You\'re Invited!! LCA Notary Network Meeting',
    body: `Hi [FIRST_NAME],

Mark your calendar!! 📅

You're invited to our upcoming LCA Notary Network Meeting!!

📅 DATE: [Add date]
🕒 TIME: [Add time] EST
💻 PLATFORM: Zoom (link to follow)

🎯 WHAT WE'LL COVER:
• Network updates and growth highlights
• Q&A with Nancy
• Best practices sharing
• Open floor for questions

This is YOUR network — we want to hear from you!!

Please RSVP by replying to this email so we know to expect you!!

Looking forward to seeing you!!

Warmly,
Nancy Phillips
Owner & CEO
Loan Closing Agent LLC`,
  },
  training_reminder: {
    label:   '📚 Training Reminder',
    subject: '📚 Quick Reminder — Important Training Available!!',
    body: `Hi [FIRST_NAME],

Just a quick reminder!!

We have important training resources available that will help you:
• Improve signing efficiency
• Reduce errors and rejections
• Stay current on best practices
• Boost your earning potential

📚 ACCESS TRAINING:
[Add link or details here]

This training is free and available 24/7 — go at your own pace!!

If you have questions, just reply to this email!!

Keep up the amazing work!! 💪

Warmly,
Nancy Phillips
Owner & CEO
Loan Closing Agent LLC`,
  },
  birthday: {
    label:   '🎂 Happy Birthday Wishes',
    subject: '🎂 Happy Birthday from the LCA Family!! 🎉',
    body: `Dear [FIRST_NAME],

🎂🎉 HAPPY BIRTHDAY!! 🎉🎂

On behalf of the entire LCA family, I want to wish you the happiest of birthdays!!

Your dedication and professionalism are what make our nationwide network the trusted choice for our clients. We're SO grateful you're part of the LCA team!!

May your year ahead be filled with:
✨ Successful signings
😊 Happy clients
💰 Abundant blessings
❤️ Joy and laughter
🎁 Wonderful surprises

Treat yourself to something special today — you deserve it!! 🥂

With love and appreciation,
Nancy Phillips
Owner & CEO
Loan Closing Agent LLC

P.S. — Happy birthday from the whole LCA family!! We're so glad to celebrate YOU today!! 🎉`,
  },
  notary_anniversary: {
    label:   '🌟 Anniversary with LCA',
    subject: '🌟 Celebrating Your LCA Anniversary!! ',
    body: `Dear [FIRST_NAME],

🌟 IT'S YOUR LCA ANNIVERSARY!! 🌟

Has it really been [X] year(s) since you joined our nationwide network?? Time flies when you're surrounded by amazing professionals!!

I just wanted to take a moment to say THANK YOU.

Thank you for:
• Your professionalism on every signing
• Your dedication to our clients
• Your loyalty to LCA
• Being part of our family

Notaries like YOU are the reason we've grown into a coast-to-coast network!!

Here's to many more years together!! 🥂

Warmly,
Nancy Phillips
Owner & CEO
Loan Closing Agent LLC`,
  },
  thanksgiving: {
    label:   '🦃 Thanksgiving Appreciation',
    subject: '🦃 Grateful for YOU This Thanksgiving!!',
    body: `Dear [FIRST_NAME],

As Thanksgiving approaches, I find myself reflecting on all the things I'm grateful for — and YOU are at the top of that list!! 🦃

Thank you for being part of our LCA family. Your professionalism, dedication, and warmth make our nationwide network feel like home!!

Wishing you and your loved ones a Thanksgiving filled with:
🦃 Delicious food
👨‍👩‍👧‍👦 Cherished family time
❤️ Beautiful memories
🙏 Endless gratitude

May your holiday be blessed!!

With heartfelt thanks,
Nancy Phillips
Owner & CEO
Loan Closing Agent LLC`,
  },
  holiday_greetings: {
    label:   '🎄 Holiday Greetings',
    subject: '🎄 Warm Holiday Wishes from LCA!! ✨',
    body: `Dear [FIRST_NAME],

As the holiday season arrives, the LCA family wants to send you our warmest wishes!! 🎄

This year has been incredible — and YOU made it possible!!

Whether you celebrate Christmas, Hanukkah, Kwanzaa, or another tradition, we wish you and your loved ones:
✨ Peace
🕯️ Light
❤️ Love
🎁 Joy
🙏 Many blessings

Thank you for being part of our nationwide network. Here's to a beautiful new year ahead!!

With love and appreciation,
Nancy Phillips & The Entire LCA Family
Loan Closing Agent LLC`,
  },
  july_4th: {
    label:   '🇺🇸 Independence Day (Veteran-Special!)',
    subject: '🇺🇸 Happy Independence Day!! ',
    body: `Dear [FIRST_NAME],

🇺🇸 HAPPY INDEPENDENCE DAY!! 🎆

As a Veteran-owned business, today holds a special place in our hearts.

We celebrate not just the freedoms we enjoy, but the men and women who served — and continue to serve — to protect those freedoms!!

Wishing you a day filled with:
🇺🇸 Patriotic pride
👨‍👩‍👧‍👦 Family time
🍔 BBQ deliciousness
🎆 Spectacular fireworks
❤️ Gratitude for our great nation

Thank you for being part of our LCA family — we're proud to serve YOU and our amazing clients across all 50 states!!

God bless America!! 🇺🇸

With patriotic pride,
Nancy & Jacob Phillips
Loan Closing Agent LLC
Veteran & Hispanic Family Owned`,
  },
}

// ═══════════════════════════════════════════════════════════════
// 🎁 LEAD EMAIL TEMPLATES (marketing_leads audience)
// ═══════════════════════════════════════════════════════════════
// 💼 CLIENT BLAST TEMPLATES — client-facing copy stays PROFESSIONAL: no emojis, no em dashes
const CLIENT_TEMPLATES = {
  welcome: {
    label: 'Welcome (new client)',
    subject: 'Welcome to Loan Closing Agent, [FIRST_NAME]',
    body: `Hi [FIRST_NAME],

Welcome to Loan Closing Agent, and thank you for trusting us with your signings.

Here is our promise to you, plain and simple: one flat, all-inclusive fee on every order. No hidden charges, no surprise add-ons, and scan-backs are always included with every client order at no cost.

Your client portal is ready whenever you are. Place an order in under two minutes, track every signing in real time, and reach us directly whenever you need a hand.

We are glad you are here, [FIRST_NAME]. Let's make your signings the easiest part of your day.

Warm regards,

Nancy Phillips
Founder and CEO
Loan Closing Agent, LLC. DBA Nationwide Notary Professionals Network
loanclosingagent.com`,
  },
  anniversary: {
    label: 'Happy Anniversary',
    subject: 'Happy anniversary, [FIRST_NAME]. Thank you for another year together',
    body: `Hi [FIRST_NAME],

Today marks another year of working together, and we did not want it to pass without saying thank you.

Partners like [COMPANY] are the reason we do what we do. Every order you have trusted us with matters to our team, and we do not take that trust lightly.

Here is to another year of smooth signings, clear communication, and one flat fee with no surprises.

Thank you for being part of our story, [FIRST_NAME].

Warm regards,

Nancy Phillips
Founder and CEO
Loan Closing Agent, LLC. DBA Nationwide Notary Professionals Network
loanclosingagent.com`,
  },
  shoutout: {
    label: 'Client Shoutout / Thank You',
    subject: 'A quick thank you from Loan Closing Agent',
    body: `Hi [FIRST_NAME],

No news, no updates, no requests. Just a genuine thank you.

Working with [COMPANY] is a pleasure for our whole team, and we wanted you to know that your business never goes unnoticed around here.

If there is ever anything we can do to make your signings even smoother, my inbox is always open.

Warm regards,

Nancy Phillips
Founder and CEO
Loan Closing Agent, LLC. DBA Nationwide Notary Professionals Network
loanclosingagent.com`,
  },
  custom: {
    label: 'Custom Message',
    subject: '',
    body: '',
  },
}

const LEAD_TEMPLATES = {
  custom: {
    label:   '✏️ Blank / Custom',
    subject: '',
    body:    '',
  },
  gift_followup: {
    label:   '🎁 Free Gift Follow-Up',
    subject: 'How are you loving your free gift?? 🎁',
    body: `Hi [FIRST_NAME],

I hope your free gift from Loan Closing Agent is already earning its keep!! 🎁

I wanted to personally check in and let you know that the gift was just the beginning. Our tribe of notary professionals gets access to:

📚 Free training courses with certificates
📖 A 200+ term notary glossary in plain English
🗓️ Professional tools built BY notaries FOR notaries
💰 Signing opportunities across all 50 states

And here's the best part: joining is completely FREE. No cost, no catch. That's our promise!!

👉 Join the Tribe: https://loanclosingagent.com/register

If you have any questions, just reply to this email — I read every single one!!

Warmly,
Nancy Phillips, Your Friendly CEO 💜
Loan Closing Agent, LLC. DBA Nationwide Notary Professionals Network`,
  },
  tribe_invite: {
    label:   '💜 Join the Tribe Invitation',
    subject: 'Your seat at the tribe is waiting!! 💜',
    body: `Hi [FIRST_NAME],

There's a seat with your name on it — and it's FREE!! 💜

The LCA Tribe is a nationwide community of notary professionals who believe in doing this work with dignity, fair pay, and zero hidden fees.

When you join (free, no cost, no catch!!), you get:

⭐ Access to signing opportunities nationwide
📚 Free courses that end in real certificates
🧰 Professional tools and resources
👥 A community that actually answers when you ask

We've been building this for 18 years — one flat fee for clients, full support for notaries, and respect at every table.

👉 Claim your seat: https://loanclosingagent.com/register

Hope to see you inside!!

Warmly,
Nancy Phillips, Your Friendly CEO 💜
Loan Closing Agent, LLC. DBA Nationwide Notary Professionals Network`,
  },
  new_resource: {
    label:   '📚 New Resource Announcement',
    subject: '📚 A brand-new free resource just dropped!!',
    body: `Hi [FIRST_NAME],

Great news — we just released something new and it's yours FREE!! 📚

🆕 WHAT'S NEW:
[Describe the new resource here]

👉 GET IT HERE:
[Add link here]

As always with LCA: no cost, no catch. We build tools that make notaries better, and we share them with the community — that's just how we do things!!

Enjoy, and keep being amazing!!

Warmly,
Nancy Phillips, Your Friendly CEO 💜
Loan Closing Agent, LLC. DBA Nationwide Notary Professionals Network`,
  },
}

// ═══════════════════════════════════════════════════════════════
// 🎯 BROADCAST PAGE COMPONENT
// ═══════════════════════════════════════════════════════════════
export default function Broadcast() {
  const { user, profile } = useAuth()
  const [notaries,        setNotaries]        = useState([])
  const [filterMode,      setFilterMode]      = useState('all')
  const [stateFilter,     setStateFilter]     = useState('FL')
  const [statusFilter,    setStatusFilter]    = useState('vetted')
  const [customSelected,  setCustomSelected]  = useState([])
  const [templateKey,     setTemplateKey]     = useState('custom')
  const [subject,         setSubject]         = useState('')
  const [body,            setBody]            = useState('')
  const [showPreview,     setShowPreview]     = useState(false)
  const [sending,         setSending]         = useState(false)
  const [showConfirm,     setShowConfirm]     = useState(false)
  const [history,         setHistory]         = useState([])
  const [activeTab,       setActiveTab]       = useState('compose') // 'compose' | 'history' | 'birthdays'
  const [birthdays,       setBirthdays]       = useState({ today: [], thisWeek: [], thisMonth: [] })
  const [leads,           setLeads]           = useState([])
  const [leadFilterMode,  setLeadFilterMode]  = useState('all')       // 'all' | 'source' | 'custom'
  const [leadSourceFilter, setLeadSourceFilter] = useState('')
  const [leadSelected,    setLeadSelected]    = useState([])
  const [leadTemplateKey, setLeadTemplateKey] = useState('custom')
  const [leadSubject,     setLeadSubject]     = useState('')
  const [leadBody,        setLeadBody]        = useState('')
  const [showLeadPreview, setShowLeadPreview] = useState(false)
  const [showLeadConfirm, setShowLeadConfirm] = useState(false)

  // 💼 CLIENT BLAST STATE
  const [clients,            setClients]            = useState([])
  const [clientFilterMode,   setClientFilterMode]   = useState('all')   // 'all' | 'state' | 'custom'
  const [clientStateFilter,  setClientStateFilter]  = useState('')
  const [clientSelected,     setClientSelected]     = useState([])
  const [clientTemplateKey,  setClientTemplateKey]  = useState('custom')
  const [clientSubject,      setClientSubject]      = useState('')
  const [clientBody,         setClientBody]         = useState('')
  const [showClientPreview,  setShowClientPreview]  = useState(false)
  const [showClientConfirm,  setShowClientConfirm]  = useState(false)

  useEffect(() => {
    loadNotaries()
    loadHistory()
    loadLeads()
    loadClients()
  }, [])

  // 🎂 Auto-switch to Birthdays tab when arrived via ?tab=birthdays (from the Command Center birthday banner)
  useEffect(() => {
    const params = new URLSearchParams(window.location.search)
    const tab = params.get('tab')
    if (tab === 'birthdays' || tab === 'history' || tab === 'compose' || tab === 'leads' || tab === 'clients') {
      setActiveTab(tab)
    }
  }, [])

  useEffect(() => {
    computeBirthdays()
  }, [notaries])

  async function loadNotaries() {
    const { data } = await supabaseAdmin
      .from('profiles')
      .select('*, notaries(*)')
      .eq('role', 'notary')
      .eq('is_approved', true)
      .order('full_name')
    setNotaries(data || [])
  }

  async function loadHistory() {
    const { data } = await supabaseAdmin
      .from('broadcasts')
      .select('*')
      .order('created_at', { ascending: false })
      .limit(50)
    setHistory(data || [])
  }

  // ═══════════ 💼 CLIENT BLAST LOGIC ═══════════
  async function loadClients() {
    const { data } = await supabaseAdmin
      .from('profiles')
      .select('*')
      .eq('role', 'client')
      .eq('is_approved', true)
      .order('created_at', { ascending: false })
    setClients(data || [])
  }

  const clientDisplayName = (c) => c.company_name || c.full_name || c.email || 'Client'
  const clientFirstName   = (c) => {
    const raw = (c.contact_name || c.full_name || '').trim()
    return raw ? raw.split(/\s+/)[0] : 'there'
  }
  const clientStateOf = (c) => ((c.state || c.business_state || '') + '').toUpperCase().trim()

  const clientStates = [...new Set(clients.map(clientStateOf).filter(Boolean))].sort()

  const clientTargeted = clients.filter(c => {
    if ((c.email || '').trim() === '') return false
    if (clientFilterMode === 'state')  return clientStateOf(c) === clientStateFilter
    if (clientFilterMode === 'custom') return clientSelected.includes(c.id)
    return true
  })
  const clientRecipientCount = clientTargeted.length

  function loadClientTemplate(key) {
    setClientTemplateKey(key)
    const tpl = CLIENT_TEMPLATES[key]
    if (tpl) { setClientSubject(tpl.subject); setClientBody(tpl.body) }
  }

  function personalizeClient(text, c) {
    return (text || '')
      .replaceAll('[FIRST_NAME]', c ? clientFirstName(c) : 'there')
      .replaceAll('[COMPANY]', c ? (c.company_name || 'your team') : 'your team')
  }

  async function sendClientsBlast(isTest = false) {
    let recipientList = []
    if (isTest) {
      recipientList = [{ email: profile?.email || user?.email, first_name: (profile?.full_name || 'Nancy').split(' ')[0] }]
      if (!recipientList[0].email) return toast.error('No admin email found for the test send!!')
    } else {
      const seen = new Set()
      clientTargeted.forEach(c => {
        const email = (c.email || '').trim().toLowerCase()
        if (!email || seen.has(email)) return
        seen.add(email)
        recipientList.push({ email, first_name: clientFirstName(c), company: c.company_name || '' })
      })
      if (recipientList.length === 0) return toast.error('No valid client emails found!!')
    }

    setSending(true)
    setShowClientConfirm(false)

    try {
      const response = await fetch('/.netlify/functions/send-broadcast', {
        method:  'POST',
        headers: { 'Content-Type': 'application/json' },
        body:    JSON.stringify({
          subject:    clientSubject,
          body:       clientBody,
          recipients: recipientList,
          isTest,
        }),
      })

      const result = await response.json()

      if (!response.ok || !result.success) {
        throw new Error(result.error || 'Failed to send clients blast')
      }

      await supabaseAdmin.from('broadcasts').insert({
        sent_by:          user?.id,
        sent_by_name:     profile?.full_name,
        subject:          clientSubject,
        body:             clientBody,
        template_type:    'client:' + clientTemplateKey,
        recipient_filter: isTest ? 'test' : `clients:${clientFilterMode}${clientFilterMode === 'state' ? ':' + clientStateFilter : ''}`,
        recipient_count:  recipientList.length,
        recipient_ids:    [],
        recipient_emails: recipientList.map(r => r.email),
        delivered_count:  result.delivered_count,
        failed_count:     result.failed_count,
        failed_emails:    result.failed_emails || [],
        status:           result.failed_count === 0 ? 'sent' : 'partial',
        is_test:          isTest,
      })

      if (isTest) toast.success('📨 Test sent to your inbox — go take a look!!')
      else toast.success(`💼 Clients blast delivered to ${result.delivered_count} client${result.delivered_count === 1 ? '' : 's'}!!`)
      loadHistory()
    } catch (err) {
      console.error('Clients blast error:', err)
      toast.error(err.message || 'Something went wrong sending the clients blast')
    } finally {
      setSending(false)
    }
  }

  async function loadLeads() {
    const { data } = await supabaseAdmin
      .from('marketing_leads')
      .select('*')
      .order('created_at', { ascending: false })
    setLeads(data || [])
  }

  // ═══════════════════════════════════════════════════════════════
  // 🎂 BIRTHDAY COMPUTATION
  // ═══════════════════════════════════════════════════════════════
  function computeBirthdays() {
    const now      = new Date()
    const todayStr = `${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`
    const today    = []
    const thisWeek = []
    const thisMonth = []

    notaries.forEach(n => {
      const bday = n.notaries?.birthday
      if (!bday) return

      const [bMonth, bDay] = bday.split('-').map(Number)
      if (!bMonth || !bDay) return

      // Today's birthday?
      if (bday === todayStr) today.push({ notary: n, date: bday })

      // This month?
      if (bMonth === now.getMonth() + 1) {
        thisMonth.push({ notary: n, date: bday, day: bDay })

        // This week? (within next 7 days)
        const bdayDate    = new Date(now.getFullYear(), bMonth - 1, bDay)
        const sevenDays   = new Date(now)
        sevenDays.setDate(sevenDays.getDate() + 7)
        if (bdayDate >= now && bdayDate <= sevenDays) {
          thisWeek.push({ notary: n, date: bday, day: bDay })
        }
      }
    })

    thisMonth.sort((a, b) => a.day - b.day)
    thisWeek.sort((a, b) => a.day - b.day)

    setBirthdays({ today, thisWeek, thisMonth })
  }

  // ═══════════════════════════════════════════════════════════════
  // 🎯 RECIPIENT FILTERING
  // ═══════════════════════════════════════════════════════════════
  function getRecipients() {
    if (filterMode === 'all') {
      return notaries
    }
    if (filterMode === 'state') {
      return notaries.filter(n => n.notaries?.commission_state === stateFilter)
    }
    if (filterMode === 'status') {
      if (statusFilter === 'vetted')   return notaries.filter(n => n.notaries?.is_vetted)
      if (statusFilter === 'elite')    return notaries.filter(n => n.notaries?.is_elite)
      if (statusFilter === 'founder')  return notaries.filter(n => n.notaries?.is_founder)
      if (statusFilter === 'ron')      return notaries.filter(n => n.notaries?.is_ron)
    }
    if (filterMode === 'custom') {
      return notaries.filter(n => customSelected.includes(n.id))
    }
    return []
  }

  function getStates() {
    const states = new Set()
    notaries.forEach(n => {
      const s = n.notaries?.commission_state
      if (s) states.add(s)
    })
    return Array.from(states).sort()
  }

  // ═══════════════════════════════════════════════════════════════
  // 🎁 LEAD FILTERING
  // ═══════════════════════════════════════════════════════════════
  function getLeadSources() {
    const sources = new Set()
    leads.forEach(l => sources.add(l.lead_source || 'unknown'))
    return Array.from(sources).sort()
  }

  function getLeadRecipients() {
    const activeLeads = leads.filter(l => !l.unsubscribed && (l.email || '').trim() !== '')
    if (leadFilterMode === 'all') {
      return activeLeads
    }
    if (leadFilterMode === 'source') {
      return activeLeads.filter(l => (l.lead_source || 'unknown') === leadSourceFilter)
    }
    if (leadFilterMode === 'custom') {
      return activeLeads.filter(l => leadSelected.includes(l.id))
    }
    return []
  }

  function leadFirstName(l) {
    if (l.first_name) return l.first_name
    if (l.full_name)  return l.full_name.split(' ')[0]
    if (l.name)       return l.name.split(' ')[0]
    return 'there'
  }

  // ═══════════════════════════════════════════════════════════════
  // ✏️ TEMPLATE LOADING
  // ═══════════════════════════════════════════════════════════════
  function loadTemplate(key) {
    setTemplateKey(key)
    const tpl = EMAIL_TEMPLATES[key]
    if (tpl) {
      setSubject(tpl.subject)
      setBody(tpl.body)
    }
  }

  function loadLeadTemplate(key) {
    setLeadTemplateKey(key)
    const tpl = LEAD_TEMPLATES[key]
    if (tpl) {
      setLeadSubject(tpl.subject)
      setLeadBody(tpl.body)
    }
  }

  // ═══════════════════════════════════════════════════════════════
  // 📤 SEND FUNCTIONS
  // ═══════════════════════════════════════════════════════════════
  async function sendBroadcast(isTest = false) {
    if (!subject.trim()) return toast.error('Subject is required!!')
    if (!body.trim())    return toast.error('Message body is required!!')

    let recipientList
    if (isTest) {
      // Test sends only to the admin
      recipientList = [{
        id:            user?.id,
        email:         profile?.email,
        full_name:     profile?.full_name,
        first_name:    profile?.full_name?.split(' ')[0],
        vendor_number: 'TEST-VENDOR',
        state:         'TEST',
      }]
    } else {
      const recipients = getRecipients()
      if (recipients.length === 0) return toast.error('No recipients selected!!')

      recipientList = recipients.map(n => ({
        id:            n.id,
        email:         n.email,
        full_name:     n.full_name,
        first_name:    n.full_name?.split(' ')[0],
        vendor_number: n.notaries?.vendor_number || '',
        state:         n.notaries?.commission_state || '',
      }))
    }

    setSending(true)
    setShowConfirm(false)

    try {
      const response = await fetch('/.netlify/functions/send-broadcast', {
        method:  'POST',
        headers: { 'Content-Type': 'application/json' },
        body:    JSON.stringify({
          subject,
          body,
          recipients: recipientList,
          isTest,
        }),
      })

      const result = await response.json()

      if (!response.ok || !result.success) {
        throw new Error(result.error || 'Failed to send broadcast')
      }

      // Save to broadcasts table
      await supabaseAdmin.from('broadcasts').insert({
        sent_by:          user?.id,
        sent_by_name:     profile?.full_name,
        subject,
        body,
        template_type:    templateKey,
        recipient_filter: isTest ? 'test' : `${filterMode}${filterMode === 'state' ? ':' + stateFilter : ''}${filterMode === 'status' ? ':' + statusFilter : ''}`,
        recipient_count:  recipientList.length,
        recipient_ids:    recipientList.map(r => r.id).filter(id => id !== user?.id),
        recipient_emails: recipientList.map(r => r.email),
        delivered_count:  result.delivered_count,
        failed_count:     result.failed_count,
        failed_emails:    result.failed_emails || [],
        status:           result.failed_count === 0 ? 'sent' : 'partial',
        is_test:          isTest,
      })

      if (isTest) {
        toast.success(`✅ Test sent to ${profile?.email}!! Check your inbox!!`)
      } else {
        toast.success(`🎉 Broadcast sent to ${result.delivered_count} notaries!!`)
        if (result.failed_count > 0) {
          toast.error(`⚠️ ${result.failed_count} email(s) failed — check history`)
        }
      }

      loadHistory()
    } catch (err) {
      toast.error('Error: ' + err.message)
    } finally {
      setSending(false)
    }
  }

  async function sendBirthdayEmail(notary) {
    const tpl = EMAIL_TEMPLATES.birthday
    const recipientList = [{
      id:            notary.id,
      email:         notary.email,
      full_name:     notary.full_name,
      first_name:    notary.full_name?.split(' ')[0],
      vendor_number: notary.notaries?.vendor_number || '',
      state:         notary.notaries?.commission_state || '',
    }]

    if (!window.confirm(`Send birthday email to ${notary.full_name}?`)) return

    setSending(true)
    try {
      const response = await fetch('/.netlify/functions/send-broadcast', {
        method:  'POST',
        headers: { 'Content-Type': 'application/json' },
        body:    JSON.stringify({
          subject:    tpl.subject,
          body:       tpl.body,
          recipients: recipientList,
          isTest:     false,
        }),
      })

      const result = await response.json()
      if (!response.ok || !result.success) throw new Error(result.error || 'Failed')

      // Mark birthday as sent for this year
      await supabase.from('notaries').update({
        birthday_email_sent_year: new Date().getFullYear(),
      }).eq('id', notary.id)

      // Log to broadcasts
      await supabaseAdmin.from('broadcasts').insert({
        sent_by:          user?.id,
        sent_by_name:     profile?.full_name,
        subject:          tpl.subject,
        body:             tpl.body,
        template_type:    'birthday',
        recipient_filter: 'birthday:' + notary.id,
        recipient_count:  1,
        recipient_ids:    [notary.id],
        recipient_emails: [notary.email],
        delivered_count:  result.delivered_count,
        failed_count:     result.failed_count,
        status:           result.failed_count === 0 ? 'sent' : 'partial',
      })

      toast.success(`🎂 Birthday email sent to ${notary.full_name}!!`)
      loadNotaries()
      loadHistory()
    } catch (err) {
      toast.error('Error: ' + err.message)
    } finally {
      setSending(false)
    }
  }

  // ═══════════════════════════════════════════════════════════════
  // 🎁 LEADS BLAST SEND
  // ═══════════════════════════════════════════════════════════════
  async function sendLeadsBlast(isTest = false) {
    if (!leadSubject.trim()) return toast.error('Subject is required!!')
    if (!leadBody.trim())    return toast.error('Message body is required!!')

    let recipientList
    if (isTest) {
      recipientList = [{
        id:            user?.id,
        email:         profile?.email,
        full_name:     profile?.full_name,
        first_name:    profile?.full_name?.split(' ')[0],
        vendor_number: '',
        state:         '',
      }]
    } else {
      const leadRecips = getLeadRecipients()
      if (leadRecips.length === 0) return toast.error('No leads selected!!')

      // De-dupe by email so nobody gets the same blast twice
      const seen = new Set()
      recipientList = []
      leadRecips.forEach(l => {
        const em = (l.email || '').trim().toLowerCase()
        if (!em || seen.has(em)) return
        seen.add(em)
        recipientList.push({
          id:            null,
          email:         em,
          full_name:     l.full_name || l.name || '',
          first_name:    leadFirstName(l),
          vendor_number: '',
          state:         '',
        })
      })
      if (recipientList.length === 0) return toast.error('No valid lead emails found!!')
    }

    setSending(true)
    setShowLeadConfirm(false)

    try {
      const response = await fetch('/.netlify/functions/send-broadcast', {
        method:  'POST',
        headers: { 'Content-Type': 'application/json' },
        body:    JSON.stringify({
          subject:    leadSubject,
          body:       leadBody,
          recipients: recipientList,
          isTest,
        }),
      })

      const result = await response.json()

      if (!response.ok || !result.success) {
        throw new Error(result.error || 'Failed to send leads blast')
      }

      await supabaseAdmin.from('broadcasts').insert({
        sent_by:          user?.id,
        sent_by_name:     profile?.full_name,
        subject:          leadSubject,
        body:             leadBody,
        template_type:    'lead:' + leadTemplateKey,
        recipient_filter: isTest ? 'test' : `leads:${leadFilterMode}${leadFilterMode === 'source' ? ':' + leadSourceFilter : ''}`,
        recipient_count:  recipientList.length,
        recipient_ids:    [],
        recipient_emails: recipientList.map(r => r.email),
        delivered_count:  result.delivered_count,
        failed_count:     result.failed_count,
        failed_emails:    result.failed_emails || [],
        status:           result.failed_count === 0 ? 'sent' : 'partial',
        is_test:          isTest,
      })

      if (isTest) {
        toast.success(`✅ Test sent to ${profile?.email}!! Check your inbox!!`)
      } else {
        toast.success(`🎁 Leads blast sent to ${result.delivered_count} lead${result.delivered_count === 1 ? '' : 's'}!!`)
        if (result.failed_count > 0) {
          toast.error(`⚠️ ${result.failed_count} email(s) failed — check history`)
        }
      }

      loadHistory()
    } catch (err) {
      toast.error('Error: ' + err.message)
    } finally {
      setSending(false)
    }
  }

  // ═══════════════════════════════════════════════════════════════
  // 🎨 RENDER
  // ═══════════════════════════════════════════════════════════════
  const recipients     = getRecipients()
  const recipientCount = recipients.length
  const states         = getStates()
  const leadRecipients     = getLeadRecipients()
  const leadRecipientCount = leadRecipients.length
  const leadSources        = getLeadSources()

  const STYLES = `
    .bcast-tabs { display:flex; gap:4px; margin-bottom:20px; border-bottom:2px solid #E8E6DF; }
    .bcast-tab { padding:10px 20px; border:none; background:transparent; cursor:pointer; font-size:13px; font-weight:500; color:#7A7870; border-bottom:3px solid transparent; margin-bottom:-2px; transition:all 0.15s; }
    .bcast-tab.active { color:#0D1B3E; border-bottom-color:#C9A84C; }
    .bcast-tab:hover { color:#0D1B3E; }
    .bcast-grid { display:grid; grid-template-columns:1fr 320px; gap:20px; align-items:start; }
    @media(max-width:900px){ .bcast-grid { grid-template-columns:1fr; } }
    .filter-chip { padding:8px 14px; border-radius:20px; font-size:12px; font-weight:500; cursor:pointer; border:1.5px solid #E8E6DF; background:#fff; color:#7A7870; transition:all 0.15s; margin-right:6px; margin-bottom:6px; display:inline-block; }
    .filter-chip.active { border-color:#C9A84C; background:#FDF6E3; color:#9A7A30; }
    .recipient-list { max-height:200px; overflow-y:auto; border:1px solid #E8E6DF; border-radius:8px; padding:8px; }
    .recipient-item { display:flex; align-items:center; gap:8px; padding:6px 8px; border-bottom:1px solid #F0EEE8; }
    .recipient-item:last-child { border-bottom:none; }
    .preview-pane { background:#F8F7F4; border:1px solid #E8E6DF; border-radius:10px; padding:20px; max-height:500px; overflow-y:auto; }
    .stats-mini { display:grid; grid-template-columns:repeat(3,1fr); gap:8px; margin-bottom:16px; }
    .stat-mini { background:#fff; border:1px solid #E8E6DF; border-radius:8px; padding:10px 12px; text-align:center; }
    .stat-mini-label { font-size:10px; color:#7A7870; letter-spacing:0.06em; }
    .stat-mini-value { font-size:18px; font-family:'Cormorant Garamond',serif; font-weight:600; color:#0D1B3E; }
    .bday-card { background:linear-gradient(135deg,#FDF6E3,#FFFBF0); border:1.5px solid #E0D9CC; border-radius:10px; padding:14px 16px; margin-bottom:10px; display:flex; align-items:center; justify-content:space-between; gap:10px; flex-wrap:wrap; }
    .bday-cake { font-size:24px; }
    .modal-overlay { position:fixed; inset:0; background:rgba(0,0,0,0.6); z-index:1000; display:flex; align-items:center; justify-content:center; padding:20px; }
    .modal-card { background:#fff; border-radius:14px; padding:28px; max-width:500px; width:100%; box-shadow:0 20px 60px rgba(0,0,0,0.3); }
  `

  return (
    <>
      <style>{STYLES}</style>
      <h1 className="page-title">📢 Email Blast Center</h1>
      <p className="page-sub">Send beautiful emails to your nationwide notary network with one click!!</p>

      {/* ═══ TABS ═══ */}
      <div className="bcast-tabs" role="tablist">
        <button className={`bcast-tab ${activeTab==='compose'?'active':''}`} onClick={() => setActiveTab('compose')}>✏️ Compose</button>
        <button className={`bcast-tab ${activeTab==='birthdays'?'active':''}`} onClick={() => setActiveTab('birthdays')}>
          🎂 Birthdays {birthdays.thisMonth.length > 0 && <span style={{ background:'#C9A84C', color:'#fff', borderRadius:10, padding:'2px 6px', fontSize:10, marginLeft:4 }}>{birthdays.thisMonth.length}</span>}
        </button>
        <button className={`bcast-tab ${activeTab==='leads'?'active':''}`} onClick={() => setActiveTab('leads')}>
          🎁 Leads Blast {leads.length > 0 && <span style={{ background:'#6B2FA0', color:'#fff', borderRadius:10, padding:'2px 6px', fontSize:10, marginLeft:4 }}>{leads.length}</span>}
        </button>
        <button className={`bcast-tab ${activeTab==='clients'?'active':''}`} onClick={() => setActiveTab('clients')}>
          💼 Clients Blast
          {clients.length > 0 && <span style={{ background:'#0D6B5C', color:'#fff', fontSize:10, fontWeight:700, padding:'1px 7px', borderRadius:10, marginLeft:6 }}>{clients.length}</span>}
        </button>
        <button className={`bcast-tab ${activeTab==='history'?'active':''}`} onClick={() => setActiveTab('history')}>📊 History</button>
      </div>

      {/* ═══ COMPOSE TAB ═══ */}
      {activeTab === 'compose' && (
        <div className="bcast-grid">
          <div>
            {/* Template Selector */}
            <div className="section-card" style={{ marginBottom:16 }}>
              <div className="section-header">
                <span className="section-title">📚 Choose a Template</span>
              </div>
              <div className="section-body">
                <select value={templateKey} onChange={e => loadTemplate(e.target.value)}>
                  {Object.entries(EMAIL_TEMPLATES).map(([key, tpl]) => (
                    <option key={key} value={key}>{tpl.label}</option>
                  ))}
                </select>
                <div style={{ fontSize:11, color:'#7A7870', marginTop:6 }}>
                  💡 Templates use [FIRST_NAME], [VENDOR], and [STATE] which auto-fill per recipient!!
                </div>
              </div>
            </div>

            {/* Compose */}
            <div className="section-card" style={{ marginBottom:16 }}>
              <div className="section-header">
                <span className="section-title">✏️ Compose Your Message</span>
              </div>
              <div className="section-body">
                <div className="form-group">
                  <label className="form-label">Subject *</label>
                  <input value={subject} onChange={e => setSubject(e.target.value)} placeholder="Subject line of your email..." />
                </div>
                <div className="form-group">
                  <label className="form-label">Message *</label>
                  <textarea value={body} onChange={e => setBody(e.target.value)} rows={14} placeholder="Write your message... Use [FIRST_NAME], [VENDOR], [STATE] for personalization!!" style={{ fontFamily:'inherit', fontSize:13, lineHeight:1.5 }} />
                </div>
                <div style={{ display:'flex', gap:8, flexWrap:'wrap' }}>
                  <button className="btn btn-outline btn-sm" onClick={() => setShowPreview(true)} disabled={!subject || !body}>👁️ Preview</button>
                  <button className="btn btn-outline btn-sm" onClick={() => sendBroadcast(true)} disabled={!subject || !body || sending}>📨 Send Test to Me</button>
                </div>
              </div>
            </div>
          </div>

          {/* Right Column — Recipient Targeting & Send */}
          <div>
            <div className="section-card" style={{ marginBottom:16 }}>
              <div className="section-header">
                <span className="section-title">🎯 Recipients</span>
              </div>
              <div className="section-body">
                <div style={{ marginBottom:12 }}>
                  <span className={`filter-chip ${filterMode==='all'?'active':''}`} onClick={() => setFilterMode('all')}>👥 All ({notaries.length})</span>
                  <span className={`filter-chip ${filterMode==='state'?'active':''}`} onClick={() => setFilterMode('state')}>🗺️ By State</span>
                  <span className={`filter-chip ${filterMode==='status'?'active':''}`} onClick={() => setFilterMode('status')}>⭐ By Status</span>
                  <span className={`filter-chip ${filterMode==='custom'?'active':''}`} onClick={() => setFilterMode('custom')}>✋ Custom</span>
                </div>

                {filterMode === 'state' && (
                  <select value={stateFilter} onChange={e => setStateFilter(e.target.value)} style={{ marginBottom:10 }}>
                    {states.map(s => <option key={s} value={s}>{s} ({notaries.filter(n => n.notaries?.commission_state === s).length})</option>)}
                  </select>
                )}

                {filterMode === 'status' && (
                  <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)} style={{ marginBottom:10 }}>
                    <option value="vetted">⭐ Vetted ({notaries.filter(n => n.notaries?.is_vetted).length})</option>
                    <option value="elite">🏆 Elite ({notaries.filter(n => n.notaries?.is_elite).length})</option>
                    <option value="founder">🏅 Founder ({notaries.filter(n => n.notaries?.is_founder).length})</option>
                    <option value="ron">💻 RON ({notaries.filter(n => n.notaries?.is_ron).length})</option>
                  </select>
                )}

                {filterMode === 'custom' && (
                  <div className="recipient-list">
                    {notaries.map(n => (
                      <div key={n.id} className="recipient-item">
                        <input
                          type="checkbox"
                          checked={customSelected.includes(n.id)}
                          onChange={e => {
                            if (e.target.checked) setCustomSelected([...customSelected, n.id])
                            else setCustomSelected(customSelected.filter(id => id !== n.id))
                          }}
                          style={{ width:'auto' }}
                        />
                        <span style={{ fontSize:12 }}>{n.full_name} <span style={{ color:'#B8B5AB' }}>({n.notaries?.commission_state || '?'})</span></span>
                      </div>
                    ))}
                  </div>
                )}

                <div style={{ background:'#FDF6E3', border:'1px solid rgba(201,168,76,0.3)', borderRadius:8, padding:'10px 12px', marginTop:12 }}>
                  <div style={{ fontSize:11, color:'#7A7870', marginBottom:2 }}>SENDING TO</div>
                  <div style={{ fontSize:18, fontFamily:'Cormorant Garamond,serif', fontWeight:700, color:'#9A7A30' }}>{recipientCount} notar{recipientCount === 1 ? 'y' : 'ies'}</div>
                </div>
              </div>
            </div>

            <button
              className="btn btn-primary"
              onClick={() => setShowConfirm(true)}
              disabled={!subject || !body || sending || recipientCount === 0}
              style={{ width:'100%', padding:'14px', fontSize:14, fontWeight:600 }}
            >
              {sending ? '⏳ Sending...' : `📤 Send Blast to ${recipientCount} Recipient${recipientCount === 1 ? '' : 's'}`}
            </button>
          </div>
        </div>
      )}

      {/* ═══ BIRTHDAYS TAB ═══ */}
      {activeTab === 'birthdays' && (
        <div>
          <div className="stats-mini">
            <div className="stat-mini">
              <div className="stat-mini-label">🎂 TODAY</div>
              <div className="stat-mini-value">{birthdays.today.length}</div>
            </div>
            <div className="stat-mini">
              <div className="stat-mini-label">📅 THIS WEEK</div>
              <div className="stat-mini-value">{birthdays.thisWeek.length}</div>
            </div>
            <div className="stat-mini">
              <div className="stat-mini-label">🗓️ THIS MONTH</div>
              <div className="stat-mini-value">{birthdays.thisMonth.length}</div>
            </div>
          </div>

          {birthdays.today.length > 0 && (
            <>
              <div style={{ fontSize:12, fontWeight:600, color:'#7A7870', letterSpacing:'0.06em', marginBottom:8 }}>🎉 BIRTHDAYS TODAY!!</div>
              {birthdays.today.map(b => (
                <div key={b.notary.id} className="bday-card">
                  <div style={{ display:'flex', alignItems:'center', gap:12 }}>
                    <div className="bday-cake">🎂</div>
                    <div>
                      <div style={{ fontSize:14, fontWeight:600, color:'#0D1B3E' }}>{b.notary.full_name}</div>
                      <div style={{ fontSize:11, color:'#7A7870' }}>{b.notary.notaries?.commission_state} · {b.notary.email}</div>
                    </div>
                  </div>
                  <button className="btn btn-primary btn-sm" onClick={() => sendBirthdayEmail(b.notary)} disabled={sending}>
                    🎁 Send Birthday Wishes!!
                  </button>
                </div>
              ))}
            </>
          )}

          {birthdays.thisMonth.length > 0 && (
            <>
              <div style={{ fontSize:12, fontWeight:600, color:'#7A7870', letterSpacing:'0.06em', marginBottom:8, marginTop:20 }}>📅 THIS MONTH'S BIRTHDAYS</div>
              {birthdays.thisMonth.map(b => {
                // 🎉 Compute today/tomorrow/past status
                const today = new Date()
                const todayMMDD = `${String(today.getMonth()+1).padStart(2,'0')}-${String(today.getDate()).padStart(2,'0')}`
                const tomorrow = new Date(today.getTime() + 86400000)
                const tomorrowMMDD = `${String(tomorrow.getMonth()+1).padStart(2,'0')}-${String(tomorrow.getDate()).padStart(2,'0')}`
                const isToday = b.date === todayMMDD
                const isTomorrow = b.date === tomorrowMMDD
                // Past birthday this month: month matches but day already passed
                const [bMonth, bDay] = b.date.split('-').map(Number)
                const isPast = bMonth === (today.getMonth()+1) && bDay < today.getDate()
                return (
                  <div key={b.notary.id} style={{
                    display:'flex',
                    alignItems:'center',
                    justifyContent:'space-between',
                    padding:'10px 14px',
                    borderBottom:'1px solid #F0EEE8',
                    flexWrap:'wrap',
                    gap:8,
                    background: isToday ? 'linear-gradient(135deg,#FDF6E3,#FCE9A8)' : 'transparent',
                    borderRadius: isToday ? 10 : 0,
                    border: isToday ? '1px solid #C9A84C' : 'none',
                    marginBottom: isToday ? 8 : 0,
                    opacity: isPast ? 0.55 : 1,
                  }}>
                    <div style={{ display:'flex', alignItems:'center', gap:12, flexWrap:'wrap' }}>
                      <div style={{
                        background: isToday ? '#C9A84C' : isPast ? '#F0EEE8' : '#FDF6E3',
                        color: isToday ? '#fff' : isPast ? '#B8B5AB' : '#9A7A30',
                        borderRadius:10,
                        padding:'6px 12px',
                        fontFamily:'Cormorant Garamond,serif',
                        fontSize:14,
                        fontWeight:600,
                      }}>
                        {b.date.replace('-', '/')}
                      </div>
                      <div>
                        <div style={{ fontSize:13, fontWeight:500, color: isPast ? '#7A7870' : '#0D1B3E', display:'flex', alignItems:'center', gap:6, flexWrap:'wrap' }}>
                          {b.notary.full_name}
                          {isToday && (
                            <span style={{ background:'#C9A84C', color:'#fff', fontSize:9, fontWeight:700, padding:'2px 8px', borderRadius:10, letterSpacing:'0.06em' }}>
                              🎉 TODAY!!
                            </span>
                          )}
                          {isTomorrow && (
                            <span style={{ background:'rgba(201,168,76,0.2)', color:'#9A7A30', fontSize:9, fontWeight:700, padding:'2px 8px', borderRadius:10, letterSpacing:'0.06em', border:'1px solid #C9A84C' }}>
                              ✨ TOMORROW
                            </span>
                          )}
                          {isPast && (
                            <span style={{ background:'#E8E6DF', color:'#7A7870', fontSize:9, fontWeight:600, padding:'2px 8px', borderRadius:10, letterSpacing:'0.06em' }}>
                              ✓ PAST
                            </span>
                          )}
                        </div>
                        <div style={{ fontSize:11, color:'#7A7870' }}>{b.notary.notaries?.commission_state}</div>
                      </div>
                    </div>
                    <button
                      className={isToday ? 'btn btn-primary btn-sm' : 'btn btn-outline btn-sm'}
                      onClick={() => sendBirthdayEmail(b.notary)}
                      disabled={sending}
                      style={{ fontSize:11 }}
                    >
                      {isToday ? '🎁 Send NOW!!' : isPast ? '🎂 Late Wish' : '🎂 Send Now'}
                    </button>
                  </div>
                )
              })}
            </>
          )}

          {birthdays.thisMonth.length === 0 && (
            <div style={{ background:'#F8F7F4', borderRadius:14, padding:32, textAlign:'center' }}>
              <div style={{ fontSize:32, marginBottom:8 }}>🎂</div>
              <div style={{ fontSize:14, color:'#7A7870', marginBottom:4 }}>No birthdays this month</div>
              <div style={{ fontSize:11, color:'#B8B5AB' }}>Set notary birthdays in their details panel on the Notaries page!!</div>
            </div>
          )}
        </div>
      )}

      {/* ═══ LEADS BLAST TAB ═══ */}
      {activeTab === 'leads' && (
        <div className="bcast-grid">
          <div>
            {/* Lead Template Selector */}
            <div className="section-card" style={{ marginBottom:16 }}>
              <div className="section-header">
                <span className="section-title">🎁 Choose a Lead Template</span>
              </div>
              <div className="section-body">
                <select value={leadTemplateKey} onChange={e => loadLeadTemplate(e.target.value)}>
                  {Object.entries(LEAD_TEMPLATES).map(([key, tpl]) => (
                    <option key={key} value={key}>{tpl.label}</option>
                  ))}
                </select>
                <div style={{ fontSize:11, color:'#7A7870', marginTop:6 }}>
                  💡 Lead templates use [FIRST_NAME] which auto-fills per lead (falls back to "there" when unknown)!!
                </div>
              </div>
            </div>

            {/* Lead Compose */}
            <div className="section-card" style={{ marginBottom:16 }}>
              <div className="section-header">
                <span className="section-title">✏️ Compose Your Leads Message</span>
              </div>
              <div className="section-body">
                <div className="form-group">
                  <label className="form-label">Subject *</label>
                  <input value={leadSubject} onChange={e => setLeadSubject(e.target.value)} placeholder="Subject line of your email..." />
                </div>
                <div className="form-group">
                  <label className="form-label">Message *</label>
                  <textarea value={leadBody} onChange={e => setLeadBody(e.target.value)} rows={14} placeholder="Write your message... Use [FIRST_NAME] for personalization!!" style={{ fontFamily:'inherit', fontSize:13, lineHeight:1.5 }} />
                </div>
                <div style={{ display:'flex', gap:8, flexWrap:'wrap' }}>
                  <button className="btn btn-outline btn-sm" onClick={() => setShowLeadPreview(true)} disabled={!leadSubject || !leadBody}>👁️ Preview</button>
                  <button className="btn btn-outline btn-sm" onClick={() => sendLeadsBlast(true)} disabled={!leadSubject || !leadBody || sending}>📨 Send Test to Me</button>
                </div>
              </div>
            </div>
          </div>

          {/* Right Column — Lead Targeting & Send */}
          <div>
            <div className="section-card" style={{ marginBottom:16 }}>
              <div className="section-header">
                <span className="section-title">🎯 Lead Recipients</span>
              </div>
              <div className="section-body">
                <div style={{ marginBottom:12 }}>
                  <span className={`filter-chip ${leadFilterMode==='all'?'active':''}`} onClick={() => setLeadFilterMode('all')}>👥 All ({leads.filter(l => !l.unsubscribed && (l.email || '').trim() !== '').length})</span>
                  <span className={`filter-chip ${leadFilterMode==='source'?'active':''}`} onClick={() => { setLeadFilterMode('source'); if (!leadSourceFilter && leadSources.length > 0) setLeadSourceFilter(leadSources[0]) }}>🏷️ By Source</span>
                  <span className={`filter-chip ${leadFilterMode==='custom'?'active':''}`} onClick={() => setLeadFilterMode('custom')}>✋ Custom</span>
                </div>

                {leadFilterMode === 'source' && (
                  <select value={leadSourceFilter} onChange={e => setLeadSourceFilter(e.target.value)} style={{ marginBottom:10 }}>
                    {leadSources.map(s => <option key={s} value={s}>{s} ({leads.filter(l => (l.lead_source || 'unknown') === s && !l.unsubscribed).length})</option>)}
                  </select>
                )}

                {leadFilterMode === 'custom' && (
                  <div className="recipient-list">
                    {leads.filter(l => !l.unsubscribed && (l.email || '').trim() !== '').map(l => (
                      <div key={l.id} className="recipient-item">
                        <input
                          type="checkbox"
                          checked={leadSelected.includes(l.id)}
                          onChange={e => {
                            if (e.target.checked) setLeadSelected([...leadSelected, l.id])
                            else setLeadSelected(leadSelected.filter(id => id !== l.id))
                          }}
                          style={{ width:'auto' }}
                        />
                        <span style={{ fontSize:12 }}>{l.email} <span style={{ color:'#B8B5AB' }}>({l.lead_source || 'unknown'})</span></span>
                      </div>
                    ))}
                  </div>
                )}

                <div style={{ background:'#F6EEFF', border:'1px solid rgba(107,47,160,0.3)', borderRadius:8, padding:'10px 12px', marginTop:12 }}>
                  <div style={{ fontSize:11, color:'#7A7870', marginBottom:2 }}>SENDING TO</div>
                  <div style={{ fontSize:18, fontFamily:'Cormorant Garamond,serif', fontWeight:700, color:'#6B2FA0' }}>{leadRecipientCount} lead{leadRecipientCount === 1 ? '' : 's'}</div>
                </div>
              </div>
            </div>

            <button
              className="btn btn-primary"
              onClick={() => setShowLeadConfirm(true)}
              disabled={!leadSubject || !leadBody || sending || leadRecipientCount === 0}
              style={{ width:'100%', padding:'14px', fontSize:14, fontWeight:600 }}
            >
              {sending ? '⏳ Sending...' : `🎁 Send Blast to ${leadRecipientCount} Lead${leadRecipientCount === 1 ? '' : 's'}`}
            </button>
          </div>
        </div>
      )}

      {/* ═══ CLIENTS BLAST TAB ═══ */}
      {activeTab === 'clients' && (
        <div className="bcast-grid">
          <div>
            {/* Client Template Selector */}
            <div className="section-card" style={{ marginBottom:16 }}>
              <div className="section-header">
                <span className="section-title">💼 Choose a Client Template</span>
              </div>
              <div className="section-body">
                <select value={clientTemplateKey} onChange={e => loadClientTemplate(e.target.value)}>
                  {Object.entries(CLIENT_TEMPLATES).map(([key, tpl]) => (
                    <option key={key} value={key}>{tpl.label}</option>
                  ))}
                </select>
                <div style={{ fontSize:11, color:'#7A7870', marginTop:6 }}>
                  💡 Use [FIRST_NAME] and [COMPANY] — they auto-fill per client. Client emails stay professional: templates carry no emojis by design!!
                </div>
              </div>
            </div>

            {/* Client Compose */}
            <div className="section-card" style={{ marginBottom:16 }}>
              <div className="section-header">
                <span className="section-title">✏️ Compose Your Clients Message</span>
              </div>
              <div className="section-body">
                <div className="form-group">
                  <label className="form-label">Subject *</label>
                  <input value={clientSubject} onChange={e => setClientSubject(e.target.value)} placeholder="Subject line of your email..." />
                </div>
                <div className="form-group">
                  <label className="form-label">Message *</label>
                  <textarea value={clientBody} onChange={e => setClientBody(e.target.value)} rows={14} placeholder="Write your message... Use [FIRST_NAME] and [COMPANY] for personalization." style={{ fontFamily:'inherit', fontSize:13, lineHeight:1.5 }} />
                </div>
                <div style={{ display:'flex', gap:8, flexWrap:'wrap' }}>
                  <button className="btn btn-outline btn-sm" onClick={() => setShowClientPreview(true)} disabled={!clientSubject || !clientBody}>👁️ Preview</button>
                  <button className="btn btn-outline btn-sm" onClick={() => sendClientsBlast(true)} disabled={!clientSubject || !clientBody || sending}>📨 Send Test to Me</button>
                </div>
              </div>
            </div>
          </div>

          {/* Right Column — Client Targeting & Send */}
          <div>
            <div className="section-card" style={{ marginBottom:16 }}>
              <div className="section-header">
                <span className="section-title">🎯 Client Recipients</span>
              </div>
              <div className="section-body">
                <div style={{ marginBottom:12 }}>
                  <span className={`filter-chip ${clientFilterMode==='all'?'active':''}`} onClick={() => setClientFilterMode('all')}>🏢 All ({clients.filter(c => (c.email || '').trim() !== '').length})</span>
                  <span className={`filter-chip ${clientFilterMode==='state'?'active':''}`} onClick={() => { setClientFilterMode('state'); if (!clientStateFilter && clientStates.length > 0) setClientStateFilter(clientStates[0]) }}>📍 By State</span>
                  <span className={`filter-chip ${clientFilterMode==='custom'?'active':''}`} onClick={() => setClientFilterMode('custom')}>✋ Custom</span>
                </div>

                {clientFilterMode === 'state' && (
                  <select value={clientStateFilter} onChange={e => setClientStateFilter(e.target.value)} style={{ marginBottom:10 }}>
                    {clientStates.map(s => <option key={s} value={s}>{s} ({clients.filter(c => clientStateOf(c) === s && (c.email || '').trim() !== '').length})</option>)}
                  </select>
                )}

                {clientFilterMode === 'custom' && (
                  <div className="recipient-list">
                    {clients.filter(c => (c.email || '').trim() !== '').map(c => (
                      <div key={c.id} className="recipient-item">
                        <input
                          type="checkbox"
                          checked={clientSelected.includes(c.id)}
                          onChange={e => {
                            if (e.target.checked) setClientSelected([...clientSelected, c.id])
                            else setClientSelected(clientSelected.filter(id => id !== c.id))
                          }}
                          style={{ width:'auto' }}
                        />
                        <span style={{ fontSize:12 }}>{clientDisplayName(c)} <span style={{ color:'#B8B5AB' }}>({c.email}{clientStateOf(c) ? ' · ' + clientStateOf(c) : ''})</span></span>
                      </div>
                    ))}
                  </div>
                )}

                <div style={{ background:'#E8F5F1', border:'1px solid rgba(13,107,92,0.3)', borderRadius:8, padding:'10px 12px', marginTop:12 }}>
                  <div style={{ fontSize:11, color:'#7A7870', marginBottom:2 }}>SENDING TO</div>
                  <div style={{ fontSize:18, fontFamily:'Cormorant Garamond,serif', fontWeight:700, color:'#0D6B5C' }}>{clientRecipientCount} client{clientRecipientCount === 1 ? '' : 's'}</div>
                </div>
              </div>
            </div>

            <button
              className="btn btn-primary"
              onClick={() => setShowClientConfirm(true)}
              disabled={!clientSubject || !clientBody || sending || clientRecipientCount === 0}
              style={{ width:'100%', padding:'14px', fontSize:14, fontWeight:600 }}
            >
              {sending ? '⏳ Sending...' : `💼 Send Blast to ${clientRecipientCount} Client${clientRecipientCount === 1 ? '' : 's'}`}
            </button>
          </div>
        </div>
      )}

      {/* 💼 CLIENT PREVIEW MODAL */}
      {showClientPreview && (
        <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.6)', zIndex:1000, display:'flex', alignItems:'center', justifyContent:'center', padding:20 }} onClick={() => setShowClientPreview(false)}>
          <div style={{ background:'#fff', borderRadius:16, padding:28, maxWidth:600, width:'100%', maxHeight:'85vh', overflowY:'auto', boxShadow:'0 20px 60px rgba(0,0,0,0.3)' }} onClick={e => e.stopPropagation()}>
            <div style={{ fontSize:12, color:'#7A7870', marginBottom:6 }}>PREVIEW — personalized sample (first targeted client)</div>
            <div style={{ border:'1px solid #E8E6DF', borderRadius:12, overflow:'hidden' }}>
              <div style={{ background:'#0D1B3E', color:'#C9A84C', padding:'14px 18px', fontWeight:700, fontSize:15 }}>
                {personalizeClient(clientSubject, clientTargeted[0])}
              </div>
              <div style={{ padding:'18px', fontSize:13, lineHeight:1.65, whiteSpace:'pre-wrap', color:'#1E2A44' }}>
                {personalizeClient(clientBody, clientTargeted[0])}
              </div>
            </div>
            <button className="btn btn-outline btn-sm" style={{ marginTop:14 }} onClick={() => setShowClientPreview(false)}>Close Preview</button>
          </div>
        </div>
      )}

      {/* 💼 CLIENT SEND CONFIRM MODAL */}
      {showClientConfirm && (
        <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.6)', zIndex:1000, display:'flex', alignItems:'center', justifyContent:'center', padding:20 }} onClick={() => setShowClientConfirm(false)}>
          <div style={{ background:'#fff', borderRadius:16, padding:28, maxWidth:440, width:'100%', boxShadow:'0 20px 60px rgba(0,0,0,0.3)' }} onClick={e => e.stopPropagation()}>
            <div style={{ fontSize:17, fontWeight:700, color:'#0D1B3E', marginBottom:10 }}>💼 Send to {clientRecipientCount} client{clientRecipientCount === 1 ? '' : 's'}?</div>
            <div style={{ fontSize:13, color:'#4A5670', lineHeight:1.6, marginBottom:8 }}>
              Subject: <strong>{clientSubject}</strong>
            </div>
            <div style={{ fontSize:12, color:'#7A7870', marginBottom:18 }}>
              Targeting: {clientFilterMode === 'all' ? 'All clients' : clientFilterMode === 'state' ? `Clients in ${clientStateFilter}` : `${clientSelected.length} hand-picked client${clientSelected.length === 1 ? '' : 's'}`}. This sends real emails to real clients.
            </div>
            <div style={{ display:'flex', gap:10 }}>
              <button className="btn btn-outline" style={{ flex:1 }} onClick={() => setShowClientConfirm(false)}>Cancel</button>
              <button className="btn btn-primary" style={{ flex:2 }} onClick={() => sendClientsBlast(false)} disabled={sending}>{sending ? '⏳ Sending...' : 'Yes, Send It'}</button>
            </div>
          </div>
        </div>
      )}

      {/* ═══ HISTORY TAB ═══ */}
      {activeTab === 'history' && (
        <div className="section-card" style={{ padding:0 }}>
          <table className="data-table">
            <thead><tr><th>Date</th><th>Subject</th><th>Type</th><th>Sent By</th><th>Recipients</th><th>Delivered</th><th>Status</th></tr></thead>
            <tbody>
              {history.length === 0 && <tr><td colSpan={7} style={{ textAlign:'center', color:'#B8B5AB', padding:24 }}>No broadcasts sent yet — send your first one!!</td></tr>}
              {history.map(b => (
                <tr key={b.id}>
                  <td style={{ fontSize:12 }}>{new Date(b.created_at).toLocaleString()}</td>
                  <td>
                    <div style={{ fontSize:12, fontWeight:500 }}>{b.subject}</div>
                    {b.is_test && <span style={{ fontSize:9, background:'#FDF6E3', color:'#8A5C00', padding:'1px 6px', borderRadius:8 }}>TEST</span>}
                  </td>
                  <td style={{ fontSize:11, color:'#7A7870' }}>{b.template_type}</td>
                  <td style={{ fontSize:12 }}>{b.sent_by_name}</td>
                  <td>{b.recipient_count}</td>
                  <td style={{ color:'#2D7A4F' }}>{b.delivered_count}</td>
                  <td>
                    {b.status === 'sent'    && <span className="badge badge-approved" style={{ fontSize:10 }}>✓ Sent</span>}
                    {b.status === 'partial' && <span className="badge badge-pending" style={{ fontSize:10 }}>⚠️ Partial</span>}
                    {b.status === 'failed'  && <span className="badge badge-rejected" style={{ fontSize:10 }}>✗ Failed</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* ═══ PREVIEW MODAL ═══ */}
      {showPreview && (
        <div className="modal-overlay" onClick={e => e.target === e.currentTarget && setShowPreview(false)}>
          <div className="modal-card" style={{ maxWidth:640 }}>
            <h2 style={{ fontFamily:'Cormorant Garamond,serif', fontSize:20, color:'#0D1B3E', marginBottom:4 }}>📧 Email Preview</h2>
            <p style={{ fontSize:11, color:'#7A7870', marginBottom:16 }}>This is how your email will look (with sample personalization)</p>
            <div className="preview-pane">
              <div style={{ background:'#0D1B3E', padding:'20px', textAlign:'center', borderRadius:'8px 8px 0 0', margin:'-20px -20px 20px -20px' }}>
                <div style={{ color:'#C9A84C', fontSize:18, fontFamily:'Cormorant Garamond,serif', fontWeight:600 }}>Loan Closing Agent LLC</div>
                <div style={{ color:'rgba(201,168,76,0.7)', fontSize:9, letterSpacing:'0.15em', marginTop:6, textTransform:'uppercase' }}>Nationwide Notary Professionals Network</div>
              </div>
              <div style={{ fontSize:14, fontWeight:600, color:'#0D1B3E', marginBottom:14, paddingBottom:8, borderBottom:'1px solid #E8E6DF' }}>
                Subject: {subject}
              </div>
              <div style={{ fontSize:13, lineHeight:1.6, color:'#0D1B3E', whiteSpace:'pre-wrap' }}>
                {body
                  .replace(/\[FIRST_NAME\]/g, 'Vera')
                  .replace(/\[FULL_NAME\]/g, 'Vera Sanchez')
                  .replace(/\[NAME\]/g, 'Vera')
                  .replace(/\[VENDOR\]/g, 'LCA-2026-0001')
                  .replace(/\[STATE\]/g, 'FL')}
              </div>
            </div>
            <button className="btn btn-primary" onClick={() => setShowPreview(false)} style={{ marginTop:16, width:'100%' }}>Close Preview</button>
          </div>
        </div>
      )}

      {/* ═══ CONFIRM MODAL ═══ */}
      {showConfirm && (
        <div className="modal-overlay" onClick={e => e.target === e.currentTarget && setShowConfirm(false)}>
          <div className="modal-card">
            <h2 style={{ fontFamily:'Cormorant Garamond,serif', fontSize:22, color:'#0D1B3E', marginBottom:4 }}>📤 Confirm Broadcast</h2>
            <p style={{ fontSize:13, color:'#7A7870', marginBottom:16 }}>Please confirm before sending — this cannot be undone!!</p>
            <div style={{ background:'#F8F7F4', borderRadius:10, padding:14, marginBottom:16 }}>
              <div style={{ fontSize:11, color:'#7A7870', marginBottom:4 }}>SUBJECT</div>
              <div style={{ fontSize:14, fontWeight:500, color:'#0D1B3E', marginBottom:10 }}>{subject}</div>
              <div style={{ fontSize:11, color:'#7A7870', marginBottom:4 }}>RECIPIENTS</div>
              <div style={{ fontSize:14, fontWeight:500, color:'#0D1B3E' }}>
                {recipientCount} notary email{recipientCount === 1 ? '' : 's'}
              </div>
              {filterMode === 'all'    && <div style={{ fontSize:11, color:'#9A7A30' }}>All approved notaries in your network</div>}
              {filterMode === 'state'  && <div style={{ fontSize:11, color:'#9A7A30' }}>Notaries in {stateFilter}</div>}
              {filterMode === 'status' && <div style={{ fontSize:11, color:'#9A7A30' }}>{statusFilter} notaries</div>}
              {filterMode === 'custom' && <div style={{ fontSize:11, color:'#9A7A30' }}>Custom selection</div>}
            </div>
            <div style={{ display:'flex', gap:10 }}>
              <button className="btn btn-outline" onClick={() => setShowConfirm(false)} disabled={sending} style={{ flex:1 }}>Cancel</button>
              <button className="btn btn-primary" onClick={() => sendBroadcast(false)} disabled={sending} style={{ flex:2 }}>
                {sending ? '⏳ Sending...' : `🚀 Send to ${recipientCount}!!`}
              </button>
            </div>
          </div>
        </div>
      )}
      {/* ═══ LEAD PREVIEW MODAL ═══ */}
      {showLeadPreview && (
        <div className="modal-overlay" onClick={e => e.target === e.currentTarget && setShowLeadPreview(false)}>
          <div className="modal-card" style={{ maxWidth:640 }}>
            <h2 style={{ fontFamily:'Cormorant Garamond,serif', fontSize:20, color:'#0D1B3E', marginBottom:4 }}>🎁 Leads Email Preview</h2>
            <p style={{ fontSize:11, color:'#7A7870', marginBottom:16 }}>This is how your email will look (with sample personalization)</p>
            <div className="preview-pane">
              <div style={{ background:'#0D1B3E', padding:'20px', textAlign:'center', borderRadius:'8px 8px 0 0', margin:'-20px -20px 20px -20px' }}>
                <div style={{ color:'#C9A84C', fontSize:18, fontFamily:'Cormorant Garamond,serif', fontWeight:600 }}>Loan Closing Agent LLC</div>
                <div style={{ color:'rgba(201,168,76,0.7)', fontSize:9, letterSpacing:'0.15em', marginTop:6, textTransform:'uppercase' }}>Nationwide Notary Professionals Network</div>
              </div>
              <div style={{ fontSize:14, fontWeight:600, color:'#0D1B3E', marginBottom:14, paddingBottom:8, borderBottom:'1px solid #E8E6DF' }}>
                Subject: {leadSubject}
              </div>
              <div style={{ fontSize:13, lineHeight:1.6, color:'#0D1B3E', whiteSpace:'pre-wrap' }}>
                {leadBody
                  .replace(/\[FIRST_NAME\]/g, 'Maria')
                  .replace(/\[FULL_NAME\]/g, 'Maria Lopez')
                  .replace(/\[NAME\]/g, 'Maria')}
              </div>
            </div>
            <button className="btn btn-primary" onClick={() => setShowLeadPreview(false)} style={{ marginTop:16, width:'100%' }}>Close Preview</button>
          </div>
        </div>
      )}

      {/* ═══ LEAD CONFIRM MODAL ═══ */}
      {showLeadConfirm && (
        <div className="modal-overlay" onClick={e => e.target === e.currentTarget && setShowLeadConfirm(false)}>
          <div className="modal-card">
            <h2 style={{ fontFamily:'Cormorant Garamond,serif', fontSize:22, color:'#0D1B3E', marginBottom:4 }}>🎁 Confirm Leads Blast</h2>
            <p style={{ fontSize:13, color:'#7A7870', marginBottom:16 }}>Please confirm before sending — this cannot be undone!!</p>
            <div style={{ background:'#F8F7F4', borderRadius:10, padding:14, marginBottom:16 }}>
              <div style={{ fontSize:11, color:'#7A7870', marginBottom:4 }}>SUBJECT</div>
              <div style={{ fontSize:14, fontWeight:500, color:'#0D1B3E', marginBottom:10 }}>{leadSubject}</div>
              <div style={{ fontSize:11, color:'#7A7870', marginBottom:4 }}>RECIPIENTS</div>
              <div style={{ fontSize:14, fontWeight:500, color:'#0D1B3E' }}>
                {leadRecipientCount} lead email{leadRecipientCount === 1 ? '' : 's'} (duplicates removed automatically)
              </div>
              {leadFilterMode === 'all'    && <div style={{ fontSize:11, color:'#6B2FA0' }}>All marketing leads</div>}
              {leadFilterMode === 'source' && <div style={{ fontSize:11, color:'#6B2FA0' }}>Leads from: {leadSourceFilter}</div>}
              {leadFilterMode === 'custom' && <div style={{ fontSize:11, color:'#6B2FA0' }}>Custom selection</div>}
            </div>
            <div style={{ display:'flex', gap:10 }}>
              <button className="btn btn-outline" onClick={() => setShowLeadConfirm(false)} disabled={sending} style={{ flex:1 }}>Cancel</button>
              <button className="btn btn-primary" onClick={() => sendLeadsBlast(false)} disabled={sending} style={{ flex:2 }}>
                {sending ? '⏳ Sending...' : `🚀 Send to ${leadRecipientCount}!!`}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  )
}
